import { Spinner } from 'flowbite-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CardMediation,
  FilterOrder,
  InputSearch,
} from '../../../components';
import { FilterOrderContext } from '../../../context/FilterOrderContext';
import { userRole } from '../../../helpers/helper';
import { getFilterOrdersOwner, getOrders } from '../../../services/apis/owner';

const MediationOrders = () => {
  const filterOrderContext = useContext(FilterOrderContext);

  let filterOrderContextCondition =
    filterOrderContext?.brands?.length == 0 &&
    filterOrderContext?.models?.length == 0 &&
    filterOrderContext?.statuses?.length == 0 &&
    filterOrderContext?.cities?.length == 0 &&
    filterOrderContext?.colors?.length == 0 &&
    !filterOrderContext?.priceFrom &&
    !filterOrderContext?.priceTo &&
    !filterOrderContext?.odoFrom &&
    !filterOrderContext?.odoTo;

  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [dates, setDates] = useState([
    { value: '', label: t('offerCycle.all') },
    { value: 'this-day', label: t('offerCycle.today') },
    { value: 'this-month', label: t('offerCycle.thisMonth') },
  ]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [period, setPeriod] = useState(null);
  const [isMyOffer, setIsMyOffer] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const observerRef = useRef(null);
  const prevFilter = useRef(filterOrderContext);

  const hasFilterChanged = useCallback(() => {
    return (
      JSON.stringify(prevFilter.current) !== JSON.stringify(filterOrderContext)
    );
  }, [filterOrderContext]);

  const navigate = useNavigate();

  const handleSearch = (e) => {
    setSearchText(decodeURIComponent(e.target.value));
  };

  const searchAction = () => {
    fetchOrders(true);
  };



  const fetchOrders = async (reset = false) => {
    setLoading(true);
    try {
      let body = {};
      if (!filterOrderContextCondition) {
        body = {
          brandIds: filterOrderContext.brands.map((brand) => brand.value),
          modelIds: filterOrderContext.models.map((model) => model.value),
          status: filterOrderContext.statuses.map((status) => status.value),
          cityIds: filterOrderContext.cities.map((city) => city.value),
          priceFrom: filterOrderContext.priceFrom,
          priceTo: filterOrderContext.priceTo,
          odoFrom: filterOrderContext.odoFrom,
          odoTo: filterOrderContext.odoTo,
          colorIds: filterOrderContext.colors.map((color) => color.value),
        };
      } else {
        body = {
          brandIds: [],
          modelIds: [],
          status: [],
          cityIds: [],
          priceFrom: null,
          priceTo: null,
          odoFrom: null,
          odoTo: null,
          colorIds: [],
        };
      }

      let data = {
        params: {
          page: reset ? 0 : currentPage,
          size: 3,
        },
        body: body,
      };
      if (period != null) {
        data.body.period = period;
      }
      if (isMyOffer) {
        data.body.containsMediatorOffers = isMyOffer;
      }
      data.body.filterOwnRequest = false;

      if (searchText) {
        data.params.searchText = searchText;
      }

      const response = await getFilterOrdersOwner(data.body, data.params);

      const newOrders = response.data.page.content;
      setOrders((prevPosts) =>
        reset ? newOrders : [...prevPosts, ...newOrders]
      );
      setHasMore(newOrders.length > 0);
      setOpenFilter(false);
      prevFilter.current = filterOrderContext;
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    if (hasFilterChanged()) {
      fetchOrders(true);
    } else if (hasMore && !loading) {
      fetchOrders();
    } else {
      fetchOrders(currentPage === 0);
    }
  }, [currentPage, period, isMyOffer]);

  const handleChangeFilter = (date) => {
    setIsMyOffer(false, date == '' ? setPeriod(null) : setPeriod(date));
    setCurrentPage(0);
    setOrders([]);
  };

  return (
    <div className="bg-gradient-to-b from-[#FFFFFF] to-[#D7E2FF] p-5">
      <div className="w-[90%] mr-auto ml-auto">
        <div className="relative">
          <InputSearch
            onChange={handleSearch}
            value={searchText}
            action={() => setOpenFilter(!openFilter)}
            showAdd={userRole == 3 ? false : true}
            showSearch={true}
            add={() => navigate('/orders/add')}
            search={() => searchAction()}
          />
          {openFilter && (
            <div className="absolute top-15 left-0 bg-white p-3 rounded-[12px] shadow-md w-[400px] max600:w-full">
              <div className="p-3 text-center text-[15px] text-[#3E0292] font-bold">
                {t('filterModal.filterWith')}
              </div>
              <FilterOrder
                from="owner"
                hideStatus={true}
                close={() => setOpenFilter(false)}
                confirm={fetchOrders}
                showColor={true}
                showOdo={true}
              />
            </div>
          )}
        </div>
        <div className="flex flex-row items-center mt-3">
          <div className="text-[#3E0292] text-[15px] font-500">
            {t('myOrders.sortBy')}
          </div>
          <div className="grid 2xl:grid-cols-5 xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 max600:grid-cols-1  gap-2 mx-2">
            {dates.map((date, index) => (
              <div
                key={index}
                className={`${period == date.value ? 'bg-[#3E0292] text-white border border-[#3E0292]' : 'bg-[#E6EDFF] text-[#3E0292]'} rounded-[12px] p-2 text-center font-500 text-[15px] cursor-pointer shadow-md`}
                onClick={() => handleChangeFilter(date.value)}
              >
                {date.label}
              </div>
            ))}
            <div
              className={`${isMyOffer == true ? 'bg-[#3E0292] text-white border border-[#3E0292]' : 'bg-[#E6EDFF] text-[#3E0292]'} rounded-[12px] p-2 text-center font-500 text-[15px] cursor-pointer shadow-md`}
              onClick={() =>
                setPeriod(
                  null,
                  setIsMyOffer(true, setCurrentPage(0, setOrders([])))
                )
              }
            >
              {t('offerCycle.myOffers')}
            </div>
          </div>
        </div>
        <div
          className={`grid md:grid-cols-2 2xl:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 sm:grid-cols-1 max600:grid-cols-1 gap-x-4 ${userRole == 3 ? 'gap-y-4' : 'gap-y-16'} my-5`}
        >
          {orders.map((order, index) => (
            <div ref={index === orders.length - 1 ? lastItemRef : null}>
              <CardMediation
                order={order}
                key={order.id}
                refreshOrders={fetchOrders}
                goEditPage={() =>
                  navigate(`/car-owner/${order?.sellMediationId}/edit`)
                }
                onClick={() =>
                  navigate(`/car-owner/${order?.sellMediationId}/details`, {
                    state: { from: 'mediationRequests' },
                  })
                }
                goOffers={() =>
                  navigate(`/car-owner/${order?.sellMediationId}/offers`)
                }
              />
            </div>
          ))}
        </div>
        {loading && (
          <div className="flex justify-center bg-[#D7E2FF69] h-[100px] items-center">
            <Spinner
              color="success"
              aria-label="Extra large spinner example"
              size="xl"
            />
          </div>
        )}
        {!hasMore && (
          <p className="mt-20 text-center text-[#3E0292]">
            {t('myOrders.loadingAllOrders')}
          </p>
        )}
      </div>
    </div>
  );
};

export default MediationOrders;
